import { debugKernel } from "../debug";
import {
  type ClientFrame,
  type HostFrame,
  parseClientFrame,
} from "../protocol/frames";
import type { Command } from "../pure/command";
import type { Effect } from "../pure/effect";
import { fold } from "../pure/fold";
import { initialWorkerState, type WorkerState } from "../pure/state";
import type { SocketAdapter } from "./socketAdapter";
import { defaultSetTimer, type SetTimer } from "./timers";
import { createTokenRelay } from "./tokenRelay";

export interface HostClientPort {
  post: (frame: HostFrame) => void;
}

export interface HostSession {
  receive: (data: unknown) => void;
  disconnect: () => void;
}

export interface KernelHost {
  connect: (port: HostClientPort) => HostSession;
}

export interface CreateHostOptions {
  adapter: SocketAdapter;
  setTimer?: SetTimer;
}

export const createHost = ({
  adapter,
  setTimer = defaultSetTimer,
}: CreateHostOptions): KernelHost => {
  let state: WorkerState = initialWorkerState;
  let cancelFlush: (() => void) | undefined;
  let nextClientId = 0;
  const ports = new Map<string, HostClientPort>();
  const queue: Command[] = [];
  let dispatching = false;

  // A port's post can throw (e.g. the window died between the fold deciding
  // to deliver and delivery happening). Treat that client as gone rather
  // than letting the throw abort the rest of the effects being applied.
  const postOrDrop = (clientId: string, frame: HostFrame) => {
    const port = ports.get(clientId);
    if (port === undefined) {
      return;
    }
    try {
      port.post(frame);
    } catch (error) {
      console.warn("[atd] dropping client whose port threw on post", error);
      ports.delete(clientId);
      dispatch({ kind: "client.disconnect", clientId });
    }
  };

  const requestFromClients = () => {
    for (const [clientId] of [...ports]) {
      postOrDrop(clientId, { type: "tokenRequest" });
    }
  };

  const relay = createTokenRelay({
    requestFromClients,
    setTimer,
  });

  const apply = (effect: Effect) => {
    switch (effect.kind) {
      case "socket.open":
        adapter.open(effect.url, relay.request);
        break;
      case "socket.close":
        adapter.close();
        break;
      case "socket.symbols":
        adapter.setSymbols(effect.symbols);
        break;
      case "quotes.deliver":
        debugKernel("worker fanout", {
          toClient: effect.clientId,
          totalClients: ports.size,
          account: effect.account,
          symbols: Object.keys(effect.quotes),
        });
        postOrDrop(effect.clientId, {
          type: "quotes",
          account: effect.account,
          quotes: effect.quotes,
        });
        break;
      case "timer.set":
        cancelFlush = setTimer(effect.delayMs, () => {
          cancelFlush = undefined;
          dispatch({ kind: "quotes.flushDue" });
        });
        break;
      case "timer.clear":
        cancelFlush?.();
        cancelFlush = undefined;
        break;
    }
  };

  // An effect can throw (e.g. the adapter it drives is in a bad state).
  // Isolate that failure to this one effect rather than letting it abort
  // the drain: an aborted drain would leave later commands (already
  // enqueued by earlier effects in this same batch, such as a nested
  // client.disconnect) sitting undrained until some unrelated future
  // dispatch happens to come along and pick them up.
  const applyEffect = (effect: Effect) => {
    try {
      apply(effect);
    } catch (error) {
      console.warn("[atd] effect failed during drain", effect.kind, error);
    }
  };

  const drain = () => {
    dispatching = true;
    try {
      for (;;) {
        const command = queue.shift();
        if (command === undefined) {
          return;
        }
        const result = fold(state, command);
        state = result.state;
        for (const effect of result.effects) {
          applyEffect(effect);
        }
      }
    } finally {
      dispatching = false;
    }
  };

  const dispatch = (command: Command) => {
    queue.push(command);
    if (dispatching) {
      return;
    }
    drain();
  };

  adapter.onQuotes((quotes) => dispatch({ kind: "quotes.received", quotes }));

  const connect = (port: HostClientPort): HostSession => {
    nextClientId += 1;
    const clientId = `client-${nextClientId}`;
    ports.set(clientId, port);
    // Before any command, so a window can tell "the worker is up" from "the
    // worker never loaded". The in-process host sends it too, which keeps the
    // two transports' observable behaviour identical.
    try {
      port.post({ type: "ready" });
    } catch (error) {
      // A port that cannot accept its first frame is not a usable client:
      // undo the registration and let the caller find out, rather than
      // silently holding a session that never works.
      ports.delete(clientId);
      throw error;
    }
    debugKernel("window attached", {
      clientId,
      totalClients: ports.size,
    });
    dispatch({ kind: "client.connect", clientId });

    const disconnect = () => {
      if (!ports.delete(clientId)) {
        return;
      }
      debugKernel("window detached", {
        clientId,
        totalClients: ports.size,
      });
      dispatch({ kind: "client.disconnect", clientId });
    };

    const handle = (frame: ClientFrame) => {
      switch (frame.type) {
        case "symbols":
          dispatch({
            kind: "client.symbols",
            clientId,
            symbols: frame.symbols,
          });
          break;
        case "visibility":
          dispatch({
            kind: "client.visibility",
            clientId,
            visible: frame.visible,
          });
          break;
        case "account":
          dispatch({
            kind: "account.report",
            account: { key: frame.key, socketUrl: frame.socketUrl },
          });
          break;
        case "token":
          relay.provide(frame.token);
          break;
        case "bye":
          disconnect();
          break;
      }
    };

    const receive = (data: unknown) => {
      const frame = parseClientFrame(data);
      if (frame !== undefined) {
        handle(frame);
      }
    };

    return {
      receive,
      disconnect,
    };
  };

  return {
    connect,
  };
};
